import React, { useState, useCallback, useRef, useEffect } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { SparkleRegular, PaintBrushRegular, CodeRegular } from '@fluentui/react-icons';
import { Chart, ChartSuggestionsData, EditChartSuggestionItem, DatasetViewColumn } from '../../api/types';
import AIEditor from './AIEditor';
import VisualEditor from './VisualEditor';
import { SqlEditorPanel } from './sqlEditor';
import CollapsiblePanel from './CollapsiblePanel';
import { type ChartEditorState } from './visualConfig';

// ── Props ──────────────────────────────────────────────────────────────────

interface EditorPanelProps {
  chart: Chart;
  previewChart?: Chart | null;
  onPreviewUpdate?: (previewKey: string, reason: string) => void;
  /** True when creating a new chart (Add Chart flow) */
  isNewChart?: boolean;
  /** Dashboard ID — passed to AIEditor for lazy preview creation */
  dashboardId?: string;
  /** Latest Redis preview key from visual editor changes (preview-update) */
  previewChartId?: string | null;
  /** Active dashboard filters — passed to AIEditor for explain skill */
  filters?: Record<string, unknown>;
  /** Pre-loaded chart suggestions from dashboard (new chart flow) */
  chartSuggestions?: ChartSuggestionsData | null;
  /** Per-chart edit suggestions (edit chart flow) */
  editSuggestions?: EditChartSuggestionItem[] | null;
  /** Editor state managed by parent */
  editorState: ChartEditorState;
  /** Initial editor state (for reset) */
  initialEditorState: ChartEditorState;
  /** Callback when editor state changes */
  onEditorStateChange: (state: ChartEditorState) => void;
  /** Series names extracted from chart data for color overrides */
  seriesNames?: string[];
  /** X-axis category names for per-category color overrides */
  categoryNames?: string[];
  /** Dataset view columns for the field mapping editor */
  viewColumns?: DatasetViewColumn[];
  /** Dashboard-level theme — passed to VisualEditor for "(Default)" indicator */
  dashboardTheme?: string;
  /** Name of the dataset view (shown in SQL editor header) */
  viewName?: string;
  /** Called when user runs SQL from the SQL editor */
  onSqlExecute: (sql: string) => void;
  /** Called when user edits SQL after a server error (clears the error) */
  onSqlErrorClear?: () => void;
  /** Whether SQL execution is in progress */
  isSqlExecuting?: boolean;
  /** SQL error to display inline in the editor */
  sqlError?: string | null;
  /**
   * Called when the Visual panel becomes hidden (collapsed).
   * Used by ChartEditPage to flush visual config to Redis preview
   * when leaving the Visual panel (only if dirty).
   */
  onTabSwitch?: (fromTab: string, toTab: string) => void;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },

  editorsRow: {
    display: 'flex',
    overflow: 'hidden',
    minHeight: 0,
  },

  /** AI panel wrapper */
  aiPanel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },

  /** Visual panel wrapper */
  visualPanel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },

  /** Vertical splitter between AI and Visual */
  verticalSplitter: {
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
    ':hover': {
      backgroundColor: tokens.colorNeutralStroke1,
    },
  },

  /** Horizontal splitter between editors and SQL */
  horizontalSplitter: {
    height: '4px',
    cursor: 'row-resize',
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
    ':hover': {
      backgroundColor: tokens.colorNeutralStroke1,
    },
  },

  /** SQL section at bottom */
  sqlSection: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },

  sqlBody: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

// ── Constants ──────────────────────────────────────────────────────────────

/** Default AI panel width as percentage of editors row (when both expanded) */
const DEFAULT_AI_SPLIT = 50;
/** Default SQL panel height as percentage of total container height */
const DEFAULT_SQL_HEIGHT = 50;

// ── Component ──────────────────────────────────────────────────────────────

/**
 * Editor panel with collapsible AI and Visual utility panels side-by-side,
 * plus a collapsible SQL section at the bottom.
 *
 * Replaces the old tabbed `EditTabs` component.
 */
const EditorPanel: React.FC<EditorPanelProps> = ({
  chart,
  previewChart,
  onPreviewUpdate,
  isNewChart,
  dashboardId,
  chartSuggestions,
  editSuggestions,
  previewChartId,
  filters,
  editorState,
  initialEditorState,
  onEditorStateChange,
  seriesNames,
  categoryNames,
  viewColumns,
  dashboardTheme,
  viewName,
  onSqlExecute,
  onSqlErrorClear,
  isSqlExecuting,
  sqlError,
  onTabSwitch,
}) => {
  const styles = useStyles();

  // ── Panel visibility state ───────────────────────────────────────────
  const [isAiCollapsed, setIsAiCollapsed] = useState(false);
  const [isVisualCollapsed, setIsVisualCollapsed] = useState(false);
  const [isSqlExpanded, setIsSqlExpanded] = useState(false);

  // ── Splitter state ───────────────────────────────────────────────────
  const [aiSplitPercent, setAiSplitPercent] = useState(DEFAULT_AI_SPLIT);
  const [sqlHeightPercent, setSqlHeightPercent] = useState(DEFAULT_SQL_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);

  // Refs for drag calculations
  const editorsRowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for drag listener cleanup on unmount
  const dragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      // Clean up any lingering drag listeners if component unmounts mid-drag
      dragCleanupRef.current?.();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // ── Track container width via ResizeObserver so pixel-based layout
  //    recomputes when the app sidebar collapses/expands ────────────────
  const [editorsRowWidth, setEditorsRowWidth] = useState(0);

  useEffect(() => {
    const el = editorsRowRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setEditorsRowWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleAiToggle = useCallback(() => {
    if (isAiCollapsed) {
      // Expanding AI
      setIsAiCollapsed(false);
    } else {
      // Collapsing AI — swap: expand Visual if it's collapsed
      if (isVisualCollapsed) {
        setIsVisualCollapsed(false);
      }
      setIsAiCollapsed(true);
    }
  }, [isAiCollapsed, isVisualCollapsed]);

  const handleVisualToggle = useCallback(() => {
    if (isVisualCollapsed) {
      // Expanding Visual
      setIsVisualCollapsed(false);
    } else {
      // Collapsing Visual — swap: expand AI if it's collapsed
      onTabSwitch?.('visual', 'ai');
      if (isAiCollapsed) {
        setIsAiCollapsed(false);
      }
      setIsVisualCollapsed(true);
    }
  }, [isVisualCollapsed, isAiCollapsed, onTabSwitch]);

  const handleSqlToggle = useCallback(() => {
    setIsSqlExpanded((prev) => !prev);
  }, []);

  // ── Vertical splitter drag (AI ↔ Visual) ─────────────────────────────

  const handleVerticalSplitterMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!editorsRowRef.current) return;
        const rect = editorsRowRef.current.getBoundingClientRect();
        const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
        setAiSplitPercent(Math.min(95, Math.max(5, pct)));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        dragCleanupRef.current = null;
        setIsDragging(false);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      dragCleanupRef.current = onMouseUp;
    },
    [],
  );

  // ── Horizontal splitter drag (Editors ↔ SQL) ─────────────────────────

  const handleHorizontalSplitterMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        // SQL height is measured from the bottom
        const distFromBottom = rect.bottom - moveEvent.clientY;
        const pct = (distFromBottom / rect.height) * 100;
        setSqlHeightPercent(Math.min(90, Math.max(10, pct)));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        dragCleanupRef.current = null;
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      dragCleanupRef.current = onMouseUp;
    },
    [],
  );

  // ── Layout computation ───────────────────────────────────────────────

  const bothExpanded = !isAiCollapsed && !isVisualCollapsed;

  // Minimum widths for each panel (px)
  const AI_MIN_WIDTH = 370;
  const VISUAL_MIN_WIDTH = 375;

  // Compute inline styles. When both panels are expanded and a panel's
  // percentage-based width drops below its minimum, the panel keeps its
  // minimum width but uses a negative margin to slide off-screen, giving
  // the visual effect of the panel sliding left/right while the other
  // panel takes the visible space.
  let aiStyle: React.CSSProperties;
  let visualStyle: React.CSSProperties;

  if (!isAiCollapsed && bothExpanded) {
    // On initial render the ResizeObserver hasn't fired yet → width = 0.
    // Fall back to percentage/flex styles until a real measurement is available.
    if (editorsRowWidth === 0) {
      aiStyle = { width: `${aiSplitPercent}%`, flex: '0 0 auto', order: 1 };
      visualStyle = { width: `${100 - aiSplitPercent}%`, flex: '0 0 auto', order: 2 };
    } else {
      const aiPx = editorsRowWidth * aiSplitPercent / 100;
      const visualPx = editorsRowWidth * (100 - aiSplitPercent) / 100;

      const aiWidth = Math.max(AI_MIN_WIDTH, aiPx);
      const visualWidth = Math.max(VISUAL_MIN_WIDTH, visualPx);

      // If AI would be smaller than its min, shift it left via negative margin
      const aiMarginLeft = aiPx < AI_MIN_WIDTH ? aiPx - AI_MIN_WIDTH : 0;
      // If Visual would be smaller than its min, shift it right via negative margin-right
      const visualMarginRight = visualPx < VISUAL_MIN_WIDTH ? visualPx - VISUAL_MIN_WIDTH : 0;

      aiStyle = {
        width: `${aiWidth}px`,
        flex: '0 0 auto',
        order: 1,
        marginLeft: aiMarginLeft !== 0 ? `${aiMarginLeft}px` : undefined,
        ...(isDragging && { transition: 'none' }),
      };
      visualStyle = {
        width: `${visualWidth}px`,
        flex: '0 0 auto',
        order: 2,
        marginRight: visualMarginRight !== 0 ? `${visualMarginRight}px` : undefined,
        ...(isDragging && { transition: 'none' }),
      };
    }
  } else if (!isAiCollapsed) {
    aiStyle = { flex: '1 1 auto', order: 1, ...(isDragging && { transition: 'none' }) };
    visualStyle = { flexShrink: 0, order: 0 };
  } else if (!isVisualCollapsed) {
    aiStyle = { flexShrink: 0, order: 1 };
    visualStyle = { flex: '1 1 auto', order: 2, ...(isDragging && { transition: 'none' }) };
  } else {
    aiStyle = { flexShrink: 0, order: 1 };
    visualStyle = { flexShrink: 0, order: 0 };
  }

  // Editors row takes remaining space after SQL; SQL takes sqlHeightPercent when expanded
  const editorsRowStyle: React.CSSProperties = isSqlExpanded
    ? { flex: `0 0 ${100 - sqlHeightPercent}%` }
    : { flex: '1 1 auto' };

  const sqlSectionStyle: React.CSSProperties = isSqlExpanded
    ? { flex: `0 0 ${sqlHeightPercent}%` }
    : { flexShrink: 0 };

  return (
    <div className={styles.container} ref={containerRef}>
      {/* ── Side-by-side editors ─────────────────────────────────────── */}
      <div className={styles.editorsRow} ref={editorsRowRef} style={editorsRowStyle}>
        {/* AI Panel */}
        <div className={styles.aiPanel} style={aiStyle}>
          <CollapsiblePanel
            title="AI Assistant"
            icon={<SparkleRegular />}
            isCollapsed={isAiCollapsed}
            onToggle={handleAiToggle}
            direction="vertical"
          >
            <AIEditor
              chart={chart}
              previewChart={previewChart}
              onPreviewUpdate={onPreviewUpdate}
              isNewChart={isNewChart}
              dashboardId={dashboardId}
              previewChartId={previewChartId}
              filters={filters}
              chartSuggestions={chartSuggestions}
              editSuggestions={editSuggestions}
            />
          </CollapsiblePanel>
        </div>

        {/* Vertical splitter — only when both panels are expanded */}
        {bothExpanded && (
          <div
            className={styles.verticalSplitter}
            style={{ order: 1 }}
            onMouseDown={handleVerticalSplitterMouseDown}
          />
        )}

        {/* Visual Panel */}
        <div className={styles.visualPanel} style={visualStyle}>
          <CollapsiblePanel
            title="Chart Builder"
            icon={<PaintBrushRegular />}
            isCollapsed={isVisualCollapsed}
            onToggle={handleVisualToggle}
            direction="vertical"
            collapseSide="right"
          >
            <VisualEditor
              chart={chart}
              previewChart={previewChart}
              editorState={editorState}
              initialEditorState={initialEditorState}
              onEditorStateChange={onEditorStateChange}
              seriesNames={seriesNames}
              categoryNames={categoryNames}
              viewColumns={viewColumns}
              dashboardTheme={dashboardTheme}
            />
          </CollapsiblePanel>
        </div>
      </div>

      {/* ── Horizontal splitter — only when SQL is expanded ──────────── */}
      {isSqlExpanded && (
        <div
          className={styles.horizontalSplitter}
          onMouseDown={handleHorizontalSplitterMouseDown}
        />
      )}

      {/* ── SQL section at bottom ────────────────────────────────────── */}
      <div className={styles.sqlSection} style={sqlSectionStyle}>
        <CollapsiblePanel
          title="Generated SQL"
          icon={<CodeRegular />}
          isCollapsed={!isSqlExpanded}
          onToggle={handleSqlToggle}
          direction="horizontal"
        >
          <div className={styles.sqlBody}>
            <SqlEditorPanel
              chart={chart}
              previewChart={previewChart}
              viewColumns={viewColumns}
              viewName={viewName}
              onSqlExecute={onSqlExecute}
              onSqlErrorClear={onSqlErrorClear}
              isExecuting={isSqlExecuting}
              sqlError={sqlError}
            />
          </div>
        </CollapsiblePanel>
      </div>
    </div>
  );
};

export default EditorPanel;